import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Avatar, Tag, Button, Spin, Select, Input, App, Modal } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  SaveOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { apiGetPatientDetails, apiUpdatePatient } from "../../api/patients";

const { TextArea } = Input;

const PatientDetails = () => {
  const { message } = App.useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [consultationNotes, setConsultationNotes] = useState("");
  const [selectedRiskLevel, setSelectedRiskLevel] = useState(1);
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  const fetchPatientsData = async () => {
    try {
      setLoading(true);
      const res = await apiGetPatientDetails(id);
      setPatientData(res.data);
      setConsultationNotes(res.data?.doctorConsultationNotes || "");
      setSelectedRiskLevel(res.data?.riskLevel);
    } catch (error) {
      console.error("Patient fetch error:", error);
      message.error("Failed to load patient data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientsData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle save consultation notes
  const handleSaveNotes = async () => {
    try {
      setIsSavingNotes(true);

      // Call the API to update consultation notes
      await apiUpdatePatient({
        patientId: parseInt(id),
        riskLevel: selectedRiskLevel ?? 1,
        doctorConsultationNotes: consultationNotes,
      });

      message.success("Consultation notes saved successfully");
      setIsEditingNotes(false);

      // Update local state
      setPatientData({
        ...patientData,
        consultationNotes: consultationNotes,
      });
    } catch (error) {
      console.error("Error saving notes:", error);
      message.error("Failed to save consultation notes");
    } finally {
      setIsSavingNotes(false);
    }
  };

  // Handle risk level change
  const handleRiskLevelChange = async (value) => {
    try {
      setSelectedRiskLevel(value);

      // API call to update risk level
      await apiUpdatePatient({
        patientId: parseInt(id),
        riskLevel: value ?? 1,
        doctorConsultationNotes: consultationNotes,
      });

      message.success("Risk level updated successfully");

      // Update local state
      setPatientData({
        ...patientData,
        riskLevel: value,
      });
    } catch (error) {
      console.error("Error updating risk level:", error);
      message.error("Failed to update risk level");
      // Revert the selection on error
      setSelectedRiskLevel(patientData?.riskLevel);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (!patientData) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <Card>
          <div className="text-center py-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Patient Not Found
            </h2>
            <p className="text-gray-500 mb-6">
              The patient with ID {id} does not exist.
            </p>
            <Button type="primary" onClick={() => navigate("/patients")}>
              Back to Patients
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header with Back Button */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/patients")}
          className="mb-4"
          type="text"
        >
          Back
        </Button>
        <h1 className="text-2xl font-semibold text-gray-800">
          Patient Details
        </h1>
        <p className="text-sm text-gray-500 mt-1">View patient details</p>
      </div>

      {/* Patient Header Card */}
      <Card className="shadow-sm mb-6 rounded-lg">
        <div className="flex items-start justify-between">
          {/* Left Section - Avatar and Info */}
          <div className="flex items-center gap-4">
            <Avatar
              size={80}
              src={patientData?.profileImage}
              style={{
                backgroundColor: patientData?.profileImage
                  ? undefined
                  : "#8B5CF6",
              }}
              className="text-2xl font-semibold"
            >
              {!patientData?.profileImage &&
                patientData?.fullName?.charAt(0)?.toUpperCase()}
            </Avatar>

            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {patientData?.fullName}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {patientData?.consultationType || "Weight Loss Consultation"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {patientData?.patientId || `Patient ${id}`}
              </p>
            </div>
          </div>

          {/* Right Section - Status, Medical Flag and Risk Level */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Status:</span>
              <Tag
                color={patientData?.isActive ? "green" : "default"}
                className="rounded-full px-3 py-1"
              >
                {patientData?.isActive ? "Active" : "Inactive"}
              </Tag>
            </div>

<div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Risk Level:</span>
              <Select
                value={selectedRiskLevel}
                onChange={handleRiskLevelChange}
                className="w-32"
                options={[
                  { value: 1, label: "Low" },
                  { value: 2, label: "Medium" },
                  { value: 3, label: "High" },
                ]}
                style={{
                  borderRadius: "20px",
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Patient Record */}
        <Card title="Patient Record" className="shadow-sm rounded-lg">
          <div className="space-y-4">
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">Age:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.age || "N/A"} years
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">Height:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.height || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">Current Weight:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.currentWeight
                  ? `${patientData.currentWeight} lbs`
                  : "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3 border-b border-gray-100">
              <span className="text-sm text-gray-600">BMI:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.bmi || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-sm text-gray-600">Goal Weight:</span>
              <span className="text-sm font-medium text-gray-900">
                {patientData?.goalWeight
                  ? `${patientData.goalWeight} lbs`
                  : "N/A"}
              </span>
            </div>
          </div>
        </Card>

        {/* Current Medications */}
        <Card title="Current Medications" className="shadow-sm rounded-lg">
          {patientData?.currentMedications &&
          patientData.currentMedications.length > 0 ? (
            <ul className="space-y-3">
              {patientData.currentMedications.map((med, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold mt-0.5">•</span>
                  <span className="text-sm text-gray-700">{med}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No current medications</p>
          )}
        </Card>
      </div>

      {/* Medical Conditions */}
      {patientData?.medicalConditions && (() => {
        const mc = patientData.medicalConditions;

        // Label map for display names and colors per category
        const CATEGORY_META = {
          kidneyConditions:              { label: "Kidney Conditions",        bg: "#FFF1F0", border: "#FFCCC7", tagColor: "#CF1322" },
          heartConditions:               { label: "Heart Conditions",          bg: "#FFF0F6", border: "#FFD6E7", tagColor: "#C41D7F" },
          metabolismDisorders:           { label: "Metabolism Disorders",      bg: "#FFFBE6", border: "#FFE58F", tagColor: "#AD6800" },
          neurologyDisorders:            { label: "Neurology Disorders",       bg: "#F0F5FF", border: "#ADC6FF", tagColor: "#1D39C4" },
          cancerHistory:                 { label: "Cancer History",            bg: "#FFF2E8", border: "#FFBB96", tagColor: "#AD2102" },
          lungDisorders:                 { label: "Lung Disorders",            bg: "#E6FFFB", border: "#87E8DE", tagColor: "#006D75" },
          jointMobilityConditions:       { label: "Joint & Mobility",          bg: "#F6FFED", border: "#B7EB8F", tagColor: "#389E0D" },
          medullaryThyroidCancerHistory: { label: "Medullary Thyroid Cancer",  bg: "#FFF0F6", border: "#FFD6E7", tagColor: "#9E1068" },
          digestiveConditions:           { label: "Digestive Conditions",      bg: "#FFFBE6", border: "#FFD591", tagColor: "#874D00" },
        };

        // Show all categories returned by the API (green ones may have empty conditions)
        const activeCategories = Object.entries(mc).filter(([, val]) =>
          val && typeof val === "object" && "flag" in val
        );

        const allEmpty = !mc || Object.keys(mc).length === 0;

        return (
          <Card className="shadow-sm rounded-xl mb-6" title="Medical Conditions">
            {allEmpty ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <span style={{ fontSize: 40 }}>✅</span>
                <p className="mt-3 text-sm font-medium">No medical conditions reported</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeCategories.map(([key, { flag, conditions }]) => {
                  const meta = CATEGORY_META[key] || { label: key, bg: "#F5F5F5", border: "#D9D9D9", tagColor: "#434343" };
                  const isRed = flag === "red";
                  return (
                    <div
                      key={key}
                      style={{ background: "#fff", border: "none", borderRadius: "12px", padding: "14px 16px" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                        <p style={{ fontSize: "11px", fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0 }}>
                          {meta.label}
                        </p>
                        <span style={{
                          fontSize: "11px", fontWeight: 700,
                          color: isRed ? "#CF1322" : "#389E0D",
                          background: isRed ? "#FFF1F0" : "#F6FFED",
                          border: "none",
                          borderRadius: "6px", padding: "1px 8px",
                        }}>
                          {isRed ? "🚩 Red" : "🟢 Green"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {conditions && conditions.length > 0 ? conditions.map((item, i) => (
                          <span key={i} style={{ background: "#fff", border: "1px solid #D1D5DB", color: "#374151", borderRadius: "6px", padding: "2px 8px", fontSize: "12px", fontWeight: 500 }}>
                            {item}
                          </span>
                        )) : (
                          <span style={{ fontSize: "12px", color: "#9CA3AF" }}>None reported</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })()}

      {/* Consultation Notes and View Appointment Button */}
      <div className="flex flex-col gap-4">
        <Card
          title="Consultation Notes"
          extra={
            <Button
              icon={isEditingNotes ? <SaveOutlined /> : <EditOutlined />}
              color="primary"
              variant="link"
              className="text-purple-600 hover:text-purple-700"
              onClick={() => {
                if (isEditingNotes) {
                  handleSaveNotes();
                } else {
                  setIsEditingNotes(true);
                }
              }}
              loading={isSavingNotes}
              disabled={isSavingNotes}
            >
              {isEditingNotes ? "Save" : "Edit"}
            </Button>
          }
          className="shadow-sm rounded-lg"
        >
          {isEditingNotes ? (
            <TextArea
              value={consultationNotes}
              onChange={(e) => setConsultationNotes(e.target.value)}
              placeholder="Enter consultation notes..."
              autoSize={{ minRows: 4, maxRows: 8 }}
              className="text-sm"
              disabled={isSavingNotes}
            />
          ) : (
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {consultationNotes ||
                "No consultation notes available. Click Edit to add notes."}
            </p>
          )}
        </Card>

        {/* Action Buttons - Aligned to Right */}
        <div className="flex justify-end gap-3">
          {patientData?.screeningPdf && (
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => setIsPdfModalOpen(true)}
              size="large"
              style={{
                borderColor: "#7C3AED",
                color: "#7C3AED",
              }}
            >
              View Screening Report
            </Button>
          )}
          <Button
            type="primary"
            icon={<CalendarOutlined />}
            onClick={() =>
              navigate(`/appointments/${patientData?.upcomingAppointment}`)
            }
            size="large"
            disabled={!patientData?.upcomingAppointment}
          >
            View Appointment
          </Button>
        </div>
      </div>

      {/* Screening PDF Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <FilePdfOutlined style={{ color: "#7C3AED" }} />
            <span>Screening Report — {patientData?.fullName}</span>
          </div>
        }
        open={isPdfModalOpen}
        onCancel={() => setIsPdfModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsPdfModalOpen(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            href={patientData?.screeningPdf}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in New Tab
          </Button>,
        ]}
        width="80vw"
        styles={{ body: { padding: 0, height: "75vh" } }}
        centered
        destroyOnClose
      >
        <iframe
          src={patientData?.screeningPdf}
          title="Screening Report PDF"
          width="100%"
          height="100%"
          style={{ border: "none", display: "block", minHeight: "75vh" }}
        />
      </Modal>
    </div>
  );
};

export default PatientDetails;
